import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, ShieldCheck, Clock, Star } from "lucide-react";
import { COMPANY, HERO_IMAGE } from "@/data/content";

const BADGES = [
  { icon: Clock, label: "Intervention 30 min" },
  { icon: ShieldCheck, label: "Devis gratuit" },
  { icon: Star, label: "4,9/5 · 127 avis" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-brand-mist pt-28 pb-16"
    >
      {/* decorative soft shapes */}
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-gold/10 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-brand-navy/5 blur-3xl" />

      <h1 className="sr-only">
        Serrurier Lagny-sur-Marne — dépannage 24h/24, ouverture de porte,
        changement de serrure et blindage à Lagny-sur-Marne (77)
      </h1>

      <motion.div
        style={{ y }}
        className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12"
      >
        <motion.div
          initial={{ opacity: 0, scale: 1.02, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-2xl overflow-hidden ring-1 ring-brand-navy/10 shadow-[0_50px_120px_-40px_rgba(10,30,63,0.5)]"
        >
          <img
            src={HERO_IMAGE}
            alt="Serrurier Lagny-sur-Marne — dépannage 24h/24, ouverture de porte et sécurisation"
            className="w-full h-auto block"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.8 }}
          className="mt-8 flex flex-col md:flex-row md:items-center gap-4"
        >
          <a
            href={`tel:${COMPANY.phoneTel}`}
            data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-3 bg-brand-gold hover:bg-brand-golddark text-brand-navy px-8 py-4 rounded-full font-semibold text-base transition-colors duration-300 active:scale-[0.97] shadow-lg shadow-brand-gold/20"
          >
            <Phone className="w-5 h-5" />
            {COMPANY.phoneDisplay}
          </a>
          <a
            href="#contact"
            data-testid="hero-quote-btn"
            className="inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navy2 text-white px-8 py-4 rounded-full font-semibold text-base transition-colors duration-300"
          >
            Devis gratuit
          </a>

          <div className="md:ml-auto flex flex-wrap gap-x-6 gap-y-2">
            {BADGES.map((b) => {
              const Icon = b.icon;
              return (
                <span
                  key={b.label}
                  className="inline-flex items-center gap-2 text-sm font-medium text-brand-navy/70"
                >
                  <Icon className="w-4 h-4 text-brand-gold" />
                  {b.label}
                </span>
              );
            })}
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex items-center gap-2 text-brand-navy/40 text-xs uppercase tracking-[0.2em]"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" /> Défiler
      </motion.div>
    </section>
  );
}
